import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useComplaints, useComplaintFilters } from '../hooks';
import { useComplaintColumns, ComplaintsDataTable, CreateComplaintDialog } from '../components';

export function ComplaintsPage() {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const user = useAuthStore((state) => state.user);
  const roleType = user?.role?.type;
  const canFilterByUser = roleType === 'PARTNER' || roleType === 'MANAGER';
  const { filters, updateFilter, updateFilters, resetFilters, handleSort } = useComplaintFilters(
    canFilterByUser ? {} : (user?.id ? { userId: user.id } : {})
  );

  const { data, isLoading, isError, error } = useComplaints(filters);

  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = useState<HTMLDivElement | null>(null);

  const columns = useComplaintColumns({
    orderBy: filters.orderBy || '',
    order: filters.order || '',
    onSort: handleSort,
    filters,
  });

  if (isError) {
    return (
      <div className="p-4">
        <p className="text-red-500">{error?.message || 'Error loading complaints'}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b-1">
        <h1 className="text-xl font-semibold">{t('complaints')}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('complaint', 'create') && <CreateComplaintDialog />}
        </div>
      </div>
      <div className="px-4 pt-4">
        <ComplaintsDataTable
          tableZoom={tableZoom}
          controlsContainer={tableControlsEl}
          data={data?.results || []}
          columns={columns}
          isLoading={isLoading}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          onClearFilters={resetFilters}
          showUserFilter={canFilterByUser}
          defaultUserId={user?.id}
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? data?.total ?? 0}
          onPageChange={(val) => updateFilter('page', val)}
          onLimitChange={(val) => updateFilters({ limit: val, page: 0 })}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
